import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
/**
 * Custom styles for a select component.
 */
const customStyles = {
    /**
     * Styles for the control container.
     * @param {any} base - The base styles.
     * @returns {object} The updated styles.
     */
    control: (base: any) => ({
        ...base,
        height: 47,
        minHeight: 47,
        borderRadius: '0.5rem',
        borderColor: '#7f7f7f',
        boxShadow: '0px 6px 10px 0px rgba(0, 0, 0, 0.1)'
    }),
    /**
     * Styles for the separator between indicators.
     * @param {any} base - The base styles.
     * @returns {object} The updated styles.
     */
    indicatorSeparator: (base: any) => ({
        ...base,
        backgroundColor: '#7f7f7f'
    }),
    /**
     * Styles for the dropdown indicator (arrow icon).
     * @param {any} base - The base styles.
     * @param {any} state - The state of the select component.
     * @returns {object} The updated styles.
     */
    dropdownIndicator: (base: any, state: any) => ({
        ...base,
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
        transition: 'transform 0.3s ease-in-out',
        color: '#7f7f7f'
    })
}

/**
 * Type predicate to check if an error is a `FetchBaseQueryError`.
 * @param {unknown} error - The error to check.
 * @returns {boolean} `true` if the error is a `FetchBaseQueryError`, otherwise `false`.
 */
export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
    return typeof error === 'object' && error != null && 'status' in error
}
const isValidFile = (file?: string): boolean => {
    if (!file) return false

    // Normalize to lowercase for comparisons
    const normalized = file.trim().toLowerCase()

    // Common placeholder patterns
    const invalidPatterns = ['-', 'na', 'n/a', 'none', 'null']

    // If the string is only dashes/spaces → invalid
    if (/^-+$/.test(normalized)) return false

    // If matches any known placeholder → invalid
    if (invalidPatterns.includes(normalized)) return false

    return true
}

/**
 * Type predicate to check if an error has a string `message` property.
 * @param {unknown} error - The error to check.
 * @returns {boolean} `true` if the error has a `message` property, otherwise `false`.
 */
export function isErrorWithMessage(error: unknown): error is { message: string } {
    return typeof error === 'object' && error != null && 'message' in error && typeof (error as any).message === 'string'
}
/**
 * Formats an ISO date string into `MMM DD, YYYY` format (e.g., `Mar 06, 2025`).
 * Adjusts the time to Mountain Daylight Time (MDT, UTC-6).
 * @param {string} isoDate - The ISO date string.
 * @returns {string} The formatted date.
 */
export const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate)

    // Define MDT offset (UTC-6)
    const MDTOffset = -6 * 60 // in minutes

    // Apply the offset to convert to MDT
    const mdtDate = new Date(date.getTime() + MDTOffset * 60 * 1000)

    // Format month name
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const month = monthNames[mdtDate.getUTCMonth()]

    // Format day
    const day = mdtDate.getUTCDate()

    // Format year
    const year = mdtDate.getUTCFullYear()

    return `${month} ${day}, ${year}`
}

/**
 * Formats a date string or Date object into `MMM DD, YYYY` format without timezone conversion.
 * @param {string | Date} date - The date input.
 * @returns {string} The formatted date, or `'-'` if the input is invalid.
 */
export const withOutMdtDateAndTime = (date: string | Date): string => {
    if (!date) {
        console.error('Invalid date input:', date)
        return '-'
    }

    const inputDate = new Date(date)
    if (isNaN(inputDate.getTime())) {
        console.error('Invalid date value:', date)
        return '-'
    }

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const month = monthNames[inputDate.getUTCMonth()]
    const day = inputDate.getUTCDate()
    const year = inputDate.getUTCFullYear()

    return `${month} ${day}, ${year}`
}
/**
 * Formats a numerical amount with comma separation and optional decimal places.
 * @param {number} amount - The amount to format.
 * @param {number} [decimalPlaces=0] - The number of decimal places to display.
 * @returns {string} The formatted amount.
 */
export const formatAmount = (amount: number, decimalPlaces: number = 0): string => {
    const roundedAmount = amount.toFixed(decimalPlaces)

    const [integerPart, fractionalPart] = roundedAmount.split('.')

    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    return fractionalPart ? `${formattedIntegerPart}.${fractionalPart}` : formattedIntegerPart
}
/**
 * Formats an ISO date string into `MMM DD, YYYY at HH:MM AM/PM` format.
 * Adjusts the time to Mountain Daylight Time (MDT, UTC-6).
 * @param {string} isoDate - The ISO date string.
 * @returns {string} The formatted date and time.
 */
export const formatDateAndTime = (isoDate: string): string => {
    const date = new Date(isoDate)

    // Define MDT offset (UTC-6)
    const MDTOffset = -6 * 60 // in minutes

    // Apply the offset to convert to MDT
    const mdtDate = new Date(date.getTime() + MDTOffset * 60 * 1000)

    // Format month name
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const month = monthNames[mdtDate.getUTCMonth()]

    // Format day
    const day = mdtDate.getUTCDate()

    // Format hours and minutes
    let hours = mdtDate.getUTCHours()
    const minutes = mdtDate.getUTCMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12
    hours = hours ? hours : 12 // Handle midnight
    const formattedTime = `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`

    // Format year
    const year = mdtDate.getUTCFullYear()

    return `${month} ${day}, ${year} at ${formattedTime}`
}

export { customStyles, isValidFile }
